/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1733702400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Dec 09 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "twilio",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Sending messages (added / changed / removed)",
          "depth": 0,
          "id": "sending-messages-added--changed--removed"
        }, {
          "value": "Recipient and Sender selection",
          "depth": 1,
          "id": "recipient-and-sender-selection"
        }, {
          "value": "Phone number cleaning",
          "depth": 1,
          "id": "phone-number-cleaning"
        }, {
          "value": "Message content",
          "depth": 1,
          "id": "message-content"
        }, {
          "value": "Templating messages",
          "depth": 0,
          "id": "templating-messages"
        }, {
          "value": "Liquid templating",
          "depth": 1,
          "id": "liquid-templating"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ol: "ol",
      li: "li",
      h3: "h3",
      code: "code",
      h4: "h4",
      a: "a",
      img: "img",
      ul: "ul",
      pre: "pre"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "With the Twilio SMS destination, Hightouch can serve as a notifier for various changes. This is a very flexible integration, but here are a few example use cases:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Send 2FA codes"
        }), "\n", _jsx(_components.li, {
          children: "Send usage notifications and alerts"
        }), "\n", _jsx(_components.li, {
          children: "Sending password reset links"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sending-messages-added--changed--removed",
        children: "Sending messages (added / changed / removed)"
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch will not send any SMS messages on the initial run to prevent a flood\nof messages to your channel. Messages will start after this initial sync."
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports sending messages based on rows that have been added, changed, or removed since the last sync run. Each row is sent as an individual SMS message and templated with Liquid."
      }), "\n", _jsx(_components.h3, {
        id: "recipient-and-sender-selection",
        children: "Recipient and Sender selection"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports both sending messages to specific static recipients, or a phone number in each row. Use the mapper to map rows or static numbers to the ", _jsx(_components.code, {
          children: "To"
        }), " and ", _jsx(_components.code, {
          children: "From"
        }), " fields."]
      }), "\n", _jsx(_components.h4, {
        id: "messaging-service-sid",
        children: "Messaging Service SID"
      }), "\n", _jsx(_components.p, {
        children: "You can also enter or select a Twilio Messaging Service SID in the mapping field."
      }), "\n", _jsxs(_components.p, {
        children: ["The Messaging Service is setup inside ", _jsx(_components.a, {
          href: "https://www.twilio.com/docs/messaging/services#send-a-message-with-a-messaging-service",
          children: "Twilio"
        }), ". It allows you to send messages from a phone number pool which is useful when you need larger message quotas."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destinations-twilio-sender-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "phone-number-cleaning",
        children: "Phone number cleaning"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch can attempt to clean phone numbers in your data to ensure they are formatted correctly in ", _jsx(_components.a, {
          href: "http://en.wikipedia.org/wiki/E.164",
          children: "E.164"
        }), " format.\nWhen cleaning phone numbers, you will need to provide a 2 letter ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/ISO_3166-2",
          children: "ISO code"
        }), " for the country the numbers belong to."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destinations-twilio-clean-phone.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "message-content",
        children: "Message content"
      }), "\n", _jsx(_components.h4, {
        id: "added-changed-and-removed-phone-numbers",
        children: "Added, changed, and removed phone numbers"
      }), "\n", _jsx(_components.p, {
        children: "For each sms, Hightouch allows you template the content you would like to send in your message (required). The row content is repeated per row in the message and has access to the row values via templating."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "When a row is added in your query results, Hightouch will send the row added content."
        }), "\n", _jsx(_components.li, {
          children: "When a row is changed in your query results, Hightouch will send the row changed content."
        }), "\n", _jsx(_components.li, {
          children: "When a row is deleted in your query results, Hightouch will send the row deleted content."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["This content can be ", _jsx(_components.a, {
          href: "#liquid-templating",
          children: "templated"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destinations-twilio-message-body.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "templating-messages",
        children: "Templating messages"
      }), "\n", _jsx(_components.h3, {
        id: "liquid-templating",
        children: "Liquid templating"
      }), "\n", _jsxs(_components.p, {
        children: ["For both the message modes, ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/",
          children: "Liquid templating "
        }), "is supported. The most important implication of this is that columns from the added or removed row can be accessed as part of the message body. Here is an example message making use of Liquid to insert a column into the message."]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Welcome to the platform {{ row[\"name\"] }}, here is your verification code {{ row[\"2fa_code\"] }}.\n"
        })
      })]
    });
  }
}
export default MDXContent;
